<script setup>
/**
 * FormControlFranchiseId
 * Logic-heavy control for franchise assignment.
 * Locked for franchise users, selectable for global admins.
 */
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import FormControlTypeRelationshipOneToMany from '../../common/form-control-type/FormControlTypeRelationshipOneToMany.vue';
import FormControlPlainText from "./FormControlPlainText.vue";

const props = defineProps({
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number],
        default: null
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State ---

const franchiseTitle = ref('');

// --- Computed ---

const franchiseId = computed(() => store.state.user.user?.franchiseId);

/**
 * Support for multi-site table naming conventions.
 */
const franchiseTableName = computed(() => {
    return store.state.sitename?.startsWith('outdoorsproject')
        ? 'odp_franchises'
        : 'franchise';
});

// --- Methods ---

/**
 * Resolves the display name of the franchise if the user is locked to one.
 */
const fetchFranchiseTitle = async () => {
    if (!franchiseId.value) return;

    try {
        const obj = await store.dispatch('itemData/get', {
            tableName: franchiseTableName.value,
            id: franchiseId.value
        });
        franchiseTitle.value = obj?.title || '';
    } catch (error) {
        console.error("Failed to fetch franchise title:", error);
    }
};

// --- Lifecycle ---

onMounted(() => {
    if (franchiseId.value) {
        // Force the form data to match the user's franchise restriction
        store.commit(`${props.formId}/presetData`, {
            name: 'franchiseId',
            value: franchiseId.value
        });

        fetchFranchiseTitle();
    }
});
</script>

<template>
    <FormControlPlainText
        v-if="franchiseId"
        :value="franchiseTitle"
        :form-id="formId"
    />

    <div v-else>
        <FormControlTypeRelationshipOneToMany
            v-bind="$props"
            v-on="$listeners"
        />
    </div>
</template>